import { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { tenantsApi } from '@/lib/api';
import { ChatCircleText, Plus, Pencil, Trash, CaretUp, CaretDown, Info, Paperclip, ListBullets, ChatText } from '@phosphor-icons/react';

const DEFAULT_CHATBOT_CONFIG = {
  enabled: true,
  signup_enabled: true,
  business_hours: { enabled: false, start: 9, end: 18 },
  away_message: "⏰ Our team is currently offline. We'll reply as soon as we're back.",
  welcome_message: (
    "👋 *Welcome to {company} Support Bot!*\n\n" +
    "Please reply with a number:\n" +
    "{menu_list}"
  ),
  unregistered_message: (
    "👋 Hi there! We couldn't find your phone number in our system.\n\n" +
    "Please register on our customer portal first:\n" +
    "{portal_url}\n\n" +
    "Once registered, message us again!"
  ),
  no_tickets_message: (
    "📭 You currently have no active service tickets.\n\n" +
    "Reply *2* to register a new ticket, or *3* to contact support."
  ),
  menu_items: [
    {
      key: "1",
      label: "Check your ticket status",
      emoji: "1️⃣",
      action: "ticket_status",
    },
    {
      key: "2",
      label: "Register a new service ticket",
      emoji: "2️⃣",
      action: "new_ticket",
      response: (
        "🆕 *Register a New Ticket*\n\n" +
        "Visit your customer portal to create a new service request:\n" +
        "🔗 {portal_url}\n\n" +
        "_Log in with your phone number to get started._"
      ),
    },
    {
      key: "3",
      label: "Contact support",
      emoji: "3️⃣",
      action: "contact_support",
      response: (
        "📞 *Contact Support*\n\n" +
        "Phone: {support_phone}\n" +
        "Email: {support_email}\n\n" +
        "Company: {company}\n" +
        "_Our team is happy to help!_"
      ),
    },
  ],
};

export const WhatsAppChatbotForm = ({ tenantConfig, setTenantConfig }) => {
  const [botConfig, setBotConfig] = useState({ ...DEFAULT_CHATBOT_CONFIG });
  const [saving, setSaving] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItemIndex, setEditingItemIndex] = useState(null);
  
  // Modal State
  const [modalKey, setModalKey] = useState('');
  const [modalLabel, setModalLabel] = useState('');
  const [modalEmoji, setModalEmoji] = useState('▪️');
  const [modalAction, setModalAction] = useState('custom');
  const [modalResponse, setModalResponse] = useState('');
  const [modalMediaType, setModalMediaType] = useState('none'); // none | image | document | video
  const [modalMediaUrl, setModalMediaUrl] = useState('');

  // Editor sub-section (keeps the form from getting too tall)
  const [editorSection, setEditorSection] = useState('menu'); // 'menu' | 'messages'

  // Interactive Live Preview State
  const [previewTab, setPreviewTab] = useState('welcome'); // 'welcome', 'response-1', etc.
  const [selectedPreviewAction, setSelectedPreviewAction] = useState(null);

  useEffect(() => {
    if (tenantConfig && tenantConfig.chatbot_config) {
      const dbConfig = tenantConfig.chatbot_config;
      setBotConfig({
        enabled: dbConfig.enabled !== undefined ? dbConfig.enabled : DEFAULT_CHATBOT_CONFIG.enabled,
        signup_enabled: dbConfig.signup_enabled !== undefined ? dbConfig.signup_enabled : DEFAULT_CHATBOT_CONFIG.signup_enabled,
        business_hours: dbConfig.business_hours || DEFAULT_CHATBOT_CONFIG.business_hours,
        away_message: dbConfig.away_message || DEFAULT_CHATBOT_CONFIG.away_message,
        welcome_message: dbConfig.welcome_message || DEFAULT_CHATBOT_CONFIG.welcome_message,
        unregistered_message: dbConfig.unregistered_message || DEFAULT_CHATBOT_CONFIG.unregistered_message,
        no_tickets_message: dbConfig.no_tickets_message || DEFAULT_CHATBOT_CONFIG.no_tickets_message,
        menu_items: dbConfig.menu_items || [...DEFAULT_CHATBOT_CONFIG.menu_items],
      });
    }
  }, [tenantConfig]);

  const brandColorAccent = tenantConfig?.brand_colour || '#002FA7';

  const handleSave = async () => {
    if (!tenantConfig?.id) {
      toast.error('Tenant ID context not found');
      return;
    }
    setSaving(true);
    try {
      const { data } = await tenantsApi.update(tenantConfig.id, {
        chatbot_config: botConfig
      });
      setTenantConfig(data);
      toast.success('WhatsApp chatbot configuration saved successfully!');
    } catch (error) {
      console.error('Failed to save chatbot config:', error);
      toast.error(error.response?.data?.detail || 'Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleOpenAddModal = () => {
    setEditingItemIndex(null);
    setModalKey('');
    setModalLabel('');
    setModalEmoji('▪️');
    setModalAction('custom');
    setModalResponse('');
    setModalMediaType('none');
    setModalMediaUrl('');
    setDialogOpen(true);
  };

  const handleOpenEditModal = (index) => {
    const item = botConfig.menu_items[index];
    setEditingItemIndex(index);
    setModalKey(item.key || '');
    setModalLabel(item.label || '');
    setModalEmoji(item.emoji || '▪️');
    setModalAction(item.action || 'custom');
    setModalResponse(item.response || '');
    setModalMediaType(item.media_type || 'none');
    setModalMediaUrl(item.media_url || '');
    setDialogOpen(true);
  };

  const handleSaveMenuItem = () => {
    if (!modalKey.trim()) {
      toast.error('Menu Key trigger is required');
      return;
    }
    if (!modalLabel.trim()) {
      toast.error('Menu Label is required');
      return;
    }

    const newItem = {
      key: modalKey.trim(),
      label: modalLabel.trim(),
      emoji: modalEmoji.trim(),
      action: modalAction,
    };

    if (modalAction !== 'ticket_status') {
      newItem.response = modalResponse;
    }

    // Attachment (brochure / video / image) — only for non-ticket actions.
    if (modalAction !== 'ticket_status' && modalMediaType !== 'none' && modalMediaUrl.trim()) {
      newItem.media_type = modalMediaType;
      newItem.media_url = modalMediaUrl.trim();
    }

    const updatedItems = [...botConfig.menu_items];
    
    // Check key duplication
    const duplicateIndex = updatedItems.findIndex((item, idx) => item.key === newItem.key && idx !== editingItemIndex);
    if (duplicateIndex !== -1) {
      toast.error(`Key "${newItem.key}" is already assigned to "${updatedItems[duplicateIndex].label}"`);
      return;
    }

    if (editingItemIndex !== null) {
      updatedItems[editingItemIndex] = newItem;
    } else {
      updatedItems.push(newItem);
    }

    // Sort by key string or number
    updatedItems.sort((a, b) => a.key.localeCompare(b.key, undefined, { numeric: true }));

    setBotConfig(prev => ({ ...prev, menu_items: updatedItems }));
    setDialogOpen(false);
    toast.success(editingItemIndex !== null ? 'Menu item updated locally' : 'Menu item added locally');
  };

  const handleDeleteMenuItem = (index) => {
    const updatedItems = botConfig.menu_items.filter((_, idx) => idx !== index);
    setBotConfig(prev => ({ ...prev, menu_items: updatedItems }));
    toast.success('Menu item deleted locally');
  };

  // Safe formatting helper for preview
  const renderMessagePreview = (templateText, selectedOptionText = null) => {
    if (!templateText) return '';
    let preview = templateText;

    const menuLines = botConfig.menu_items.map(
      item => `${item.emoji || '▪️'}  ${item.label}`
    ).join('\n');

    const variables = {
      company: tenantConfig?.company_name || 'Kawaki Coolers',
      support_phone: tenantConfig?.support_phone || '+91 1234567890',
      support_email: tenantConfig?.support_email || 'support@kawakicoolers.com',
      portal_url: tenantConfig?.customer_portal_url || 'https://kwicc.kawakiindia.com',
      menu_list: menuLines,
    };

    Object.entries(variables).forEach(([k, v]) => {
      preview = preview.replace(new RegExp(`{${k}}`, 'g'), v);
    });

    // Bold formatting
    preview = preview
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
    preview = preview.replace(/\*([^*]+)\*/g, '<strong>$1</strong>');

    return (
      <div className="space-y-4">
        {selectedOptionText && (
          <div className="bg-[#e2f7cb] p-2.5 rounded-lg shadow-sm text-xs font-normal text-slate-800 leading-relaxed max-w-[80%] border border-emerald-100 whitespace-pre-wrap ml-auto relative">
            <strong>{selectedOptionText}</strong>
            <div className="text-[9px] text-[#5c6870] text-right mt-1">
              <span>{new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
            </div>
          </div>
        )}
        <div className="bg-white p-3 rounded-lg shadow-sm text-xs font-normal text-slate-800 leading-relaxed max-w-[85%] border border-slate-100 whitespace-pre-wrap font-sans relative">
          <span dangerouslySetInnerHTML={{ __html: preview }} />
          <div className="text-[9px] text-[#5c6870] text-right mt-1 flex items-center justify-end gap-1">
            <span>{new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
            <span className="text-[#34b7f1] font-bold">✓✓</span>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between pb-4 border-b border-slate-100">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-primary/10 rounded-sm text-primary">
            <ChatCircleText size={24} style={{ color: brandColorAccent }} />
          </div>
          <div>
            <h3 className="text-xl font-bold text-slate-850 tracking-tight">WhatsApp Chatbot</h3>
            <p className="text-slate-500 text-xs mt-0.5">
              Configure your self-service support menu and automated responses on WhatsApp.
            </p>
          </div>
        </div>
        <Button
          type="button"
          onClick={handleSave}
          disabled={saving}
          className="font-bold text-xs uppercase tracking-wider text-white"
          style={{ backgroundColor: brandColorAccent }}
        >
          {saving ? 'Saving...' : 'Save Configuration'}
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        {/* Editor Side */}
        <div className="lg:col-span-7 space-y-6">
          {/* Enabled Switch */}
          <div className="flex items-center justify-between p-4 border rounded-lg bg-slate-50/50">
            <div>
              <Label htmlFor="bot-enabled-switch" className="font-bold text-slate-800 text-sm">Enable WhatsApp Chatbot</Label>
              <p className="text-xs text-slate-500 mt-0.5">Allow users to message the bot to get instant help, check ticket status, and more.</p>
            </div>
            <Switch
              id="bot-enabled-switch"
              checked={botConfig.enabled}
              onCheckedChange={(checked) => setBotConfig(prev => ({ ...prev, enabled: checked }))}
            />
          </div>

          {botConfig.enabled && (
            <>
              {/* Sub-section switcher keeps the editor compact */}
              <div className="flex bg-slate-100 p-1 rounded-md w-full">
                {[
                  { key: 'menu', label: 'Menu Options', icon: ListBullets },
                  { key: 'messages', label: 'System Messages', icon: ChatText },
                ].map((s) => {
                  const Ico = s.icon;
                  return (
                    <button
                      key={s.key}
                      type="button"
                      onClick={() => setEditorSection(s.key)}
                      className={`flex-1 flex items-center justify-center gap-1.5 text-xs font-bold uppercase tracking-wider py-2 rounded-sm transition ${editorSection === s.key ? 'bg-white shadow-sm text-slate-800' : 'text-slate-500'}`}
                    >
                      <Ico size={14} weight="bold" /> {s.label}
                    </button>
                  );
                })}
              </div>

              {/* Menu Items Table */}
              {editorSection === 'menu' && (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h4 className="text-sm font-bold text-slate-800">Menu Trigger Options</h4>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleOpenAddModal}
                    className="h-8 font-bold text-xs uppercase tracking-wider text-primary border-primary hover:bg-primary/5 gap-1"
                    style={{ borderColor: brandColorAccent, color: brandColorAccent }}
                  >
                    <Plus size={14} /> Add Item
                  </Button>
                </div>

                <div className="border rounded-md overflow-hidden bg-white">
                  <table className="w-full text-left border-collapse text-xs">
                    <thead>
                      <tr className="bg-slate-50 border-b border-slate-200 text-slate-500 font-bold uppercase tracking-wider">
                        <th className="py-2.5 px-3 w-[60px] text-center">Key</th>
                        <th className="py-2.5 px-3 w-[50px] text-center">Emoji</th>
                        <th className="py-2.5 px-3">Label</th>
                        <th className="py-2.5 px-3">System Action</th>
                        <th className="py-2.5 px-3 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 font-medium text-slate-700">
                      {botConfig.menu_items.length === 0 ? (
                        <tr>
                          <td colSpan={5} className="py-6 text-center text-slate-400 font-medium italic">
                            No menu items defined. Click "Add Item" to start.
                          </td>
                        </tr>
                      ) : (
                        botConfig.menu_items.map((item, index) => (
                          <tr key={index} className="hover:bg-slate-50/30 transition-colors">
                            <td className="py-2.5 px-3 text-center font-bold font-mono text-slate-900">
                              {item.key}
                            </td>
                            <td className="py-2.5 px-3 text-center text-base">
                              {item.emoji}
                            </td>
                            <td className="py-2.5 px-3 font-semibold text-slate-800">
                              {item.label}
                            </td>
                            <td className="py-2.5 px-3">
                              <div className="flex items-center gap-1.5">
                                <Badge variant="secondary" className="font-bold text-[10px] uppercase tracking-wider">
                                  {item.action.replace('_', ' ')}
                                </Badge>
                                {item.media_url && (
                                  <Badge className="bg-violet-100 text-violet-700 font-bold text-[9px] uppercase gap-0.5 px-1.5">
                                    <Paperclip size={9} weight="bold" /> {item.media_type}
                                  </Badge>
                                )}
                              </div>
                            </td>
                            <td className="py-2.5 px-3 text-right whitespace-nowrap space-x-1.5">
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                onClick={() => handleOpenEditModal(index)}
                                className="h-7 w-7 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                              >
                                <Pencil size={14} />
                              </Button>
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                onClick={() => handleDeleteMenuItem(index)}
                                className="h-7 w-7 text-red-600 hover:text-red-700 hover:bg-red-50"
                              >
                                <Trash size={14} />
                              </Button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
              )}

              {/* Text Templates */}
              {editorSection === 'messages' && (
              <div className="space-y-4">
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="welcome-msg" className="text-xs font-bold uppercase tracking-wider text-slate-700">Welcome Message Template</Label>
                    <Badge variant="outline" className="font-mono text-[9px] lowercase text-slate-500">Variables: {'{company}'}, {'{menu_list}'}</Badge>
                  </div>
                  <Textarea
                    id="welcome-msg"
                    rows={4}
                    value={botConfig.welcome_message}
                    onChange={(e) => setBotConfig(prev => ({ ...prev, welcome_message: e.target.value }))}
                    className="font-mono text-xs focus:ring-1 focus:ring-primary focus:border-primary"
                    placeholder="Enter welcome message..."
                  />
                </div>

                <div className="space-y-4">
                  {/* In-chat sign-up toggle */}
                  <div className="flex items-center justify-between p-3 border rounded-lg bg-slate-50/50">
                    <div className="pr-3">
                      <Label htmlFor="signup-switch" className="font-bold text-slate-800 text-sm">Sign up new customers in chat</Label>
                      <p className="text-xs text-slate-500 mt-0.5">
                        When an unknown number messages, the bot creates their account right in WhatsApp (asks their name — the phone is already verified) instead of sending them to the portal.
                      </p>
                    </div>
                    <Switch
                      id="signup-switch"
                      checked={botConfig.signup_enabled}
                      onCheckedChange={(checked) => setBotConfig(prev => ({ ...prev, signup_enabled: checked }))}
                    />
                  </div>

                  {/* Business hours + away message (for agent handoff) */}
                  <div className="p-3 border rounded-lg space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="pr-3">
                        <Label className="font-bold text-slate-800 text-sm">Staffed hours (IST)</Label>
                        <p className="text-xs text-slate-500 mt-0.5">When a customer asks for an agent outside these hours, the away message is added to the reply.</p>
                      </div>
                      <Switch
                        checked={!!botConfig.business_hours?.enabled}
                        onCheckedChange={(v) => setBotConfig(prev => ({ ...prev, business_hours: { ...prev.business_hours, enabled: v } }))}
                      />
                    </div>
                    {botConfig.business_hours?.enabled && (
                      <>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-slate-500">From</span>
                          <Input type="number" min={0} max={23} value={botConfig.business_hours?.start ?? 9}
                            onChange={(e) => setBotConfig(prev => ({ ...prev, business_hours: { ...prev.business_hours, start: parseInt(e.target.value, 10) || 0 } }))}
                            className="text-sm w-20" />
                          <span className="text-xs text-slate-500">:00 to</span>
                          <Input type="number" min={0} max={23} value={botConfig.business_hours?.end ?? 18}
                            onChange={(e) => setBotConfig(prev => ({ ...prev, business_hours: { ...prev.business_hours, end: parseInt(e.target.value, 10) || 0 } }))}
                            className="text-sm w-20" />
                          <span className="text-xs text-slate-500">:00</span>
                        </div>
                        <Textarea
                          rows={2}
                          value={botConfig.away_message}
                          onChange={(e) => setBotConfig(prev => ({ ...prev, away_message: e.target.value }))}
                          className="font-mono text-xs"
                          placeholder="Away message shown outside staffed hours…"
                        />
                      </>
                    )}
                  </div>

                  <div className="space-y-1.5">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="unregistered-msg" className="text-xs font-bold uppercase tracking-wider text-slate-700">
                        Unregistered User Message {botConfig.signup_enabled && <span className="text-slate-400 normal-case font-normal">(fallback — only used when in-chat sign-up is off)</span>}
                      </Label>
                      <Badge variant="outline" className="font-mono text-[9px] lowercase text-slate-500">{'{portal_url}'}</Badge>
                    </div>
                    <Textarea
                      id="unregistered-msg"
                      rows={4}
                      value={botConfig.unregistered_message}
                      onChange={(e) => setBotConfig(prev => ({ ...prev, unregistered_message: e.target.value }))}
                      className={`font-mono text-xs focus:ring-1 focus:ring-primary focus:border-primary ${botConfig.signup_enabled ? 'opacity-60' : ''}`}
                      placeholder="Enter unregistered response..."
                    />
                  </div>

                  <div className="space-y-1.5">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="no-tickets-msg" className="text-xs font-bold uppercase tracking-wider text-slate-700">No Active Tickets Response</Label>
                    </div>
                    <Textarea
                      id="no-tickets-msg"
                      rows={4}
                      value={botConfig.no_tickets_message}
                      onChange={(e) => setBotConfig(prev => ({ ...prev, no_tickets_message: e.target.value }))}
                      className="font-mono text-xs focus:ring-1 focus:ring-primary focus:border-primary"
                      placeholder="Enter response when active tickets count is 0..."
                    />
                  </div>
                </div>
              </div>
              )}
            </>
          )}
        </div>

        {/* Live Preview Side */}
        <div className="lg:col-span-5 flex flex-col space-y-4">
          <Label className="text-xs font-bold uppercase tracking-wider text-slate-700">Live WhatsApp Chat Simulator</Label>
          <div className="flex-1 border border-slate-200 rounded-lg overflow-hidden bg-[#efeae2] flex flex-col min-h-[420px] relative shadow-inner">
            <div className="absolute inset-0 opacity-[0.03] pointer-events-none" style={{ backgroundImage: 'radial-gradient(circle, #000 10%, transparent 11%)', backgroundSize: '12px 12px' }}></div>
            
            {/* Header profile info */}
            <div className="bg-[#075e54] text-white p-3 flex items-center gap-2.5 relative z-10 shadow-md">
              <div className="w-8 h-8 rounded-full bg-emerald-500/20 border border-white/20 flex items-center justify-center font-bold text-xs uppercase text-emerald-100">
                {tenantConfig?.company_name?.substring(0, 2) || 'WA'}
              </div>
              <div>
                <div className="font-bold text-xs leading-none">{tenantConfig?.company_name || 'Support Bot'}</div>
                <div className="text-[9px] text-emerald-100/80 mt-0.5">Online • Automated Support</div>
              </div>
            </div>

            {/* Chats Container */}
            <div className="flex-1 p-4 overflow-y-auto relative z-10 space-y-4">
              {!botConfig.enabled ? (
                <div className="h-full flex items-center justify-center text-center p-6 text-slate-400 italic text-xs">
                  Chatbot is currently disabled
                </div>
              ) : previewTab === 'welcome' ? (
                renderMessagePreview(botConfig.welcome_message)
              ) : previewTab === 'unregistered' ? (
                botConfig.signup_enabled
                  ? renderMessagePreview(
                      `👋 *Welcome to {company}!*\n\nLet's get you set up right here — no need to leave WhatsApp.\n\nWhat's your *full name*?\n\n_Reply *cancel* to stop._`,
                      "hello"
                    )
                  : renderMessagePreview(botConfig.unregistered_message, "hello")
              ) : previewTab === 'no_tickets' ? (
                renderMessagePreview(botConfig.no_tickets_message, "1")
              ) : (
                // Selected item preview — mirror what the bot ACTUALLY does per action.
                (() => {
                  const item = botConfig.menu_items.find(i => i.key === previewTab);
                  if (!item) return renderMessagePreview(botConfig.welcome_message);

                  const FLOW_PREVIEWS = {
                    ticket_status: "🎫 *Your Active Tickets (1):*\n\n🔧 *TKT-1082* — In Progress\n    _AC filter maintenance review_\n\n_Reply with a number to continue._",
                    new_ticket: "🆕 *New Service Request*\n\nWhich product needs service?\n\n*1.* Your Product (S/N: ABC-123)\n\n_Reply *cancel* to stop, or *menu* for the main menu._",
                    register_product: "📦 *Register a Product*\n\nPlease send the *serial number* printed on your product (or its box/label).\n\n_Reply *cancel* to stop, or *menu* for the main menu._",
                    talk_to_agent: item.response || "🙋 *Connecting you with a support agent.*\n\nSomeone from our team will reply here shortly.\n_Reply *menu* anytime to go back to the automated assistant._",
                  };

                  const isFlow = item.action === 'new_ticket' || item.action === 'register_product';
                  const customResponse = FLOW_PREVIEWS[item.action] || item.response || 'Action response template empty.';

                  return (
                    <>
                      {renderMessagePreview(customResponse, item.key)}
                      {isFlow && (
                        <div className="text-[10px] text-slate-500 bg-amber-50 border border-amber-200 rounded-md px-2.5 py-1.5 mx-auto max-w-[90%]">
                          ⚡ This is an <strong>interactive flow</strong> — the bot asks follow-up questions and {item.action === 'new_ticket' ? 'creates the ticket' : 'registers the product'} in-chat.
                          {item.action === 'new_ticket' && ' If the customer has no registered products, it falls back to the Response Template below.'}
                          {item.media_url ? '' : ''}
                        </div>
                      )}
                      {item.media_url && (
                        <div className="text-[10px] text-violet-700 bg-violet-50 border border-violet-200 rounded-md px-2.5 py-1.5 mx-auto max-w-[90%]">
                          📎 Also sends a <strong>{item.media_type}</strong> attachment.
                        </div>
                      )}
                    </>
                  );
                })()
              )}
            </div>

            {/* Quick interactive actions (Simulate clicks) */}
            {botConfig.enabled && (
              <div className="bg-white/80 backdrop-blur-sm p-3 border-t border-slate-200 relative z-10 flex flex-wrap gap-1.5 justify-center">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setPreviewTab('welcome')}
                  className={`h-7 px-2.5 text-[10px] font-bold uppercase tracking-wider ${previewTab === 'welcome' ? 'bg-[#dcf8c6] border-emerald-300' : ''}`}
                >
                  Start
                </Button>
                {botConfig.menu_items.map((item, idx) => (
                  <Button
                    key={idx}
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setPreviewTab(item.key)}
                    className={`h-7 px-2.5 text-[10px] font-bold uppercase tracking-wider ${previewTab === item.key ? 'bg-[#dcf8c6] border-emerald-300' : ''}`}
                  >
                    Reply "{item.key}"
                  </Button>
                ))}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setPreviewTab('unregistered')}
                  className={`h-7 px-2.5 text-[10px] font-bold uppercase tracking-wider ${previewTab === 'unregistered' ? 'bg-[#dcf8c6] border-emerald-300' : ''}`}
                >
                  Unregistered
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setPreviewTab('no_tickets')}
                  className={`h-7 px-2.5 text-[10px] font-bold uppercase tracking-wider ${previewTab === 'no_tickets' ? 'bg-[#dcf8c6] border-emerald-300' : ''}`}
                >
                  No Tickets
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Add/Edit Menu Item Modal */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-[500px] bg-white">
          <DialogHeader>
            <DialogTitle className="text-base font-black text-slate-800 tracking-tight">
              {editingItemIndex !== null ? 'Edit Menu Option' : 'Add Menu Option'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-3">
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="menu-key" className="text-xs font-bold text-slate-700">Trigger Key</Label>
                <Input
                  id="menu-key"
                  placeholder="e.g. 1"
                  value={modalKey}
                  onChange={(e) => setModalKey(e.target.value)}
                  className="font-mono text-center font-bold"
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="menu-emoji" className="text-xs font-bold text-slate-700">Emoji Icon</Label>
                <Input
                  id="menu-emoji"
                  placeholder="e.g. 1️⃣"
                  value={modalEmoji}
                  onChange={(e) => setModalEmoji(e.target.value)}
                  className="text-center text-lg"
                />
              </div>

              <div className="space-y-1.5 col-span-1">
                <Label htmlFor="menu-action" className="text-xs font-bold text-slate-700">Action Type</Label>
                <Select value={modalAction} onValueChange={setModalAction}>
                  <SelectTrigger className="bg-white text-xs font-semibold">
                    <SelectValue placeholder="Action" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ticket_status">Ticket Status</SelectItem>
                    <SelectItem value="new_ticket">New Ticket (in-chat)</SelectItem>
                    <SelectItem value="register_product">Register Product (in-chat)</SelectItem>
                    <SelectItem value="contact_support">Contact Info</SelectItem>
                    <SelectItem value="talk_to_agent">Talk to Agent</SelectItem>
                    <SelectItem value="custom">Custom Reply</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="menu-label" className="text-xs font-bold text-slate-700">Menu Item Description (shown on lists)</Label>
              <Input
                id="menu-label"
                placeholder="e.g. Check active ticket status"
                value={modalLabel}
                onChange={(e) => setModalLabel(e.target.value)}
                className="font-semibold text-slate-800"
              />
            </div>

            {/* Contextual explanation of what the chosen action actually does */}
            {(() => {
              const ACTION_HELP = {
                ticket_status: 'Looks up the customer\'s active tickets and replies with their live status. No response template needed.',
                new_ticket: 'Runs the interactive in-chat ticket flow (pick product → issue → describe → confirm → creates the ticket). The template below is only sent as a fallback if the customer has no registered products.',
                register_product: 'Runs the interactive in-chat registration flow (ask serial → confirm → registers the product). No response template is used.',
                contact_support: 'Sends the response template below (your contact details).',
                talk_to_agent: 'Hands the conversation to a human agent (bot goes silent). The template below is the confirmation sent to the customer.',
                custom: 'Sends exactly the response template below.',
              };
              return (
                <div className="flex items-start gap-2 rounded-md bg-blue-50 border border-blue-100 px-3 py-2">
                  <Info size={14} className="text-blue-500 shrink-0 mt-0.5" />
                  <p className="text-[11px] text-blue-800 leading-relaxed">{ACTION_HELP[modalAction] || ACTION_HELP.custom}</p>
                </div>
              );
            })()}

            {modalAction !== 'ticket_status' && modalAction !== 'register_product' && (
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <Label htmlFor="menu-response" className="text-xs font-bold text-slate-700">
                    {modalAction === 'new_ticket'
                      ? 'Fallback Message (no registered products)'
                      : modalAction === 'talk_to_agent'
                      ? 'Handoff Confirmation Message'
                      : 'Response Template'}
                  </Label>
                  <Badge variant="outline" className="font-mono text-[9px] lowercase text-slate-500">
                    Supports {'{company}'}, {'{portal_url}'}, {'{support_phone}'}
                  </Badge>
                </div>
                <Textarea
                  id="menu-response"
                  rows={4}
                  value={modalResponse}
                  onChange={(e) => setModalResponse(e.target.value)}
                  className="font-mono text-xs focus:ring-1 focus:ring-primary focus:border-primary"
                  placeholder="Type the response message sent back to the customer..."
                />
              </div>
            )}

            {(modalAction === 'custom' || modalAction === 'contact_support') && (
              <div className="space-y-2 rounded-lg border border-slate-200 bg-slate-50/60 p-3">
                <div className="flex items-center gap-1.5">
                  <Paperclip size={14} className="text-slate-500" />
                  <Label className="text-xs font-bold text-slate-700">Attachment <span className="font-normal text-slate-400">— send a brochure, image or video</span></Label>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <Select value={modalMediaType} onValueChange={setModalMediaType}>
                    <SelectTrigger className="bg-white text-xs font-semibold col-span-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No attachment</SelectItem>
                      <SelectItem value="image">Image</SelectItem>
                      <SelectItem value="document">Document / PDF</SelectItem>
                      <SelectItem value="video">Video</SelectItem>
                    </SelectContent>
                  </Select>
                  <Input
                    placeholder="https://…  (public media URL)"
                    value={modalMediaUrl}
                    onChange={(e) => setModalMediaUrl(e.target.value)}
                    disabled={modalMediaType === 'none'}
                    className="text-xs col-span-2 disabled:opacity-50"
                  />
                </div>
                {modalMediaType !== 'none' && (
                  <p className="text-[10px] text-slate-400">The message above is sent as the caption. Use a direct, publicly reachable file URL.</p>
                )}
              </div>
            )}
          </div>

          <DialogFooter className="gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setDialogOpen(false)}
              className="text-xs font-bold uppercase tracking-wider"
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleSaveMenuItem}
              className="text-xs font-bold uppercase tracking-wider text-white"
              style={{ backgroundColor: brandColorAccent }}
            >
              Apply Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
